import { z } from 'zod';
import { AgentErrorCodeSchema } from './error-code.js';
import { ArtifactSchema } from '../artifact.js';
import { AgentToolCallStartedSchema, AgentToolCallResultSchema } from './tool-call.js';
import { AgentProposalPayloadSchema } from './proposal.js';

// ---- v2 SSE event variants --------------------------------------------------
//
// Every server-sent event from `POST /api/dashboards/agent/v2/ask` is one of
// these ten shapes. The client switches on `type` and handles each branch;
// unknown types and extra fields are rejected (`.strict()`) so a stale client
// fails loudly. `final` is the terminal event of a successful run; `error` is
// terminal and indicates the run did not complete.

const ConversationLoadedEventSchema = z
  .object({
    type: z.literal('conversation_loaded'),
    conversationId: z.string().uuid(),
    title: z.string(),
    messageCount: z.number().int().nonnegative(),
  })
  .strict();

const ThinkingEventSchema = z
  .object({
    type: z.literal('thinking'),
    label: z.string().max(200),
  })
  .strict();

const ToolCallStartedEventSchema = z
  .object({
    type: z.literal('tool_call_started'),
    call: AgentToolCallStartedSchema,
  })
  .strict();

const ToolCallResultEventSchema = z
  .object({
    type: z.literal('tool_call_result'),
    result: AgentToolCallResultSchema,
  })
  .strict();

const ArtifactCreatedEventSchema = z
  .object({
    type: z.literal('artifact_created'),
    artifact: ArtifactSchema,
  })
  .strict();

const ClarificationRequestedEventSchema = z
  .object({
    type: z.literal('clarification_requested'),
    question: z.string().max(2000),
  })
  .strict();

// WHY (#2006, epic #2004): glossary/memory proposals share one payload shape,
//   but each event pins `proposal.kind` to its own literal so a glossary event
//   cannot carry a memory proposal (or vice-versa) at parse-time. `.extend()`
//   preserves the base `.strict()` unknown-key policy in Zod 3.
const GlossaryProposalCreatedEventSchema = z
  .object({
    type: z.literal('glossary_proposal_created'),
    proposal: AgentProposalPayloadSchema.extend({ kind: z.literal('glossary') }),
  })
  .strict();

const MemoryProposalCreatedEventSchema = z
  .object({
    type: z.literal('memory_proposal_created'),
    proposal: AgentProposalPayloadSchema.extend({ kind: z.literal('memory') }),
  })
  .strict();

// WHY (#2006, epic #2004): verdict/provenance is intentionally omitted here to
//   keep the v2 final answer SQL-free — the per-tool evidence trail lives in
//   `tool_call_result` (a v2-native confidence field can be added additively
//   later by #2012 if needed).
const FinalEventSchema = z
  .object({
    type: z.literal('final'),
    conversationId: z.string().uuid(),
    text: z.string(),
  })
  .strict();

// WHY (#2006, epic #2004): the v2 error event is intentionally minimal and
//   self-contained — it does NOT import v1 stage vocabulary. `code` is the
//   agent-scoped `AgentErrorCodeSchema` (not the full `ErrorCode` enum) so a
//   non-agent code can never reach the client; the client branches on a stable
//   code, not message text.
const ErrorEventSchema = z
  .object({
    type: z.literal('error'),
    code: AgentErrorCodeSchema,
    message: z.string(),
  })
  .strict();

/**
 * @aiPurpose discriminated union of all SSE event payloads streamed by the v2
 *   agent run endpoint (`POST /api/dashboards/agent/v2/ask`). Consumers MUST
 *   switch on `type` and handle every one of the ten variants. `final` is the
 *   terminal event of a successful run; `error` is terminal and indicates the
 *   run did not complete.
 * @aiInvariant New event types require a coordinated shared + backend + web
 *   change (append-only discriminant set). Each variant is `.strict()`, so an
 *   unknown `type` or extra field is rejected at parse-time.
 * @aiSecurity tool-call and proposal payloads are structurally incapable of
 *   carrying raw row dumps / secrets (see `./tool-call.js`, `./proposal.js`).
 */
export const AgentV2EventSchema = z.discriminatedUnion('type', [
  ConversationLoadedEventSchema,
  ThinkingEventSchema,
  ToolCallStartedEventSchema,
  ToolCallResultEventSchema,
  ArtifactCreatedEventSchema,
  ClarificationRequestedEventSchema,
  GlossaryProposalCreatedEventSchema,
  MemoryProposalCreatedEventSchema,
  FinalEventSchema,
  ErrorEventSchema,
]);

export type AgentV2Event = z.infer<typeof AgentV2EventSchema>;
